"use client";

import {
  startTransition,
  useEffect,
  useRef,
  useState,
  useActionState,
} from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";

import {
  ArrowRight,
  Loader2,
  CheckCircle2,
  Building2,
  Award,
  Users,
  User,
  Mail,
  Globe,
  Landmark,
  BadgeEuro,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/toast";

// ── Server-Actions ─────────────────────────────────────────
import {
  register,
  type RegisterActionState,
  getCompanyByDomain,
  getCompanyByID,
  getCompanyUserCount,
} from "@/app/(auth)/actions";
// ───────────────────────────────────────────────────────────

type Step = 0 | 1 | 2 | 3 | 4;

export default function RegisterPage() {
  const router = useRouter();
  const t = useTranslations("auth");

  // Simulation tasks with translation keys
  const simulationTasks = [
    { key: "visitWebsite", label: t("simulation.visitWebsite") },
    { key: "researchPurpose", label: t("simulation.researchPurpose") },
    { key: "analyzeServices", label: t("simulation.analyzeServices") },
    { key: "researchPublications", label: t("simulation.researchPublications") },
    { key: "gatherData", label: t("simulation.gatherData") },
  ];

  /* UI-State */
  const [step, setStep] = useState<Step>(0);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  /* Company- und User-Metadaten */
  const [companyData, setCompanyData] = useState<any>(null);
  const [userType, setUserType] = useState<"rare" | "common">("common");
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  /* Aktion für Register */
  const [stateRaw, formAction, isPending] = useActionState<
    RegisterActionState,
    FormData
  >(register, { status: "idle" });

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), [step]);

  const variants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  } as const;

  /* ──────────────── Step-Handler ──────────────── */
  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim()) {
      setError("");
      setStep(1);
    } else {
      setError(t("enterName"));
    }
  };

  const handleCredsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || password.length < 8) {
      setError(t("enterValidEmail"));
      return;
    }
    setError("");
    setStep(2);
    await runSimulation();
  };

  /* ───────── Simulation + echte Daten ─────────── */
  const runSimulation = async () => {
    const domain = email.split("@")[1]?.toLowerCase() ?? "";

    /* 1) Fake-Progress  */
    for (const task of simulationTasks) {
      await new Promise((r) => setTimeout(r, 1000));
      setCompletedSteps((p) => [...p, task.key]);
    }

    /* 2) Company-ID via Domain  */
    const id = await getCompanyByDomain(domain);
    if (!id) return setStep(3); // Closed-Beta-Fallback

    /* 3) Daten + Usercount parallel holen */
    const [rawData, userCount] = await Promise.all([
      getCompanyByID(id),
      getCompanyUserCount(id),
    ]);

    const company: any = (rawData as any)?.companyProfile ?? rawData;

    if (company.coreCompanyIdentifiers && !company.core_company_identifiers) {
      company.core_company_identifiers = company.coreCompanyIdentifiers;
    }
    if (
      company.core_company_identifiers?.headquartersAddress &&
      !company.core_company_identifiers.headquarters_address
    ) {
      company.core_company_identifiers.headquarters_address =
        company.core_company_identifiers.headquartersAddress;
    }
    setCompanyData(company);
    setUserType(userCount === 0 ? "rare" : "common");
    setStep(3);
  };

  /* Konto anlegen → Step 4 + Register-Action */
  const handleCreateAccount = () => {
    if (!companyData) return;
    setStep(4);
    createAccount();
  };

  const createAccount = () => {
    const domain = email.split("@")[1]?.toLowerCase() ?? "";

    const fd = new FormData();
    fd.set("email", email);
    fd.set("password", password);
    fd.set(
      "companyName",
      companyData?.core_company_identifiers?.company_name ?? ""
    );
    fd.set("domain", domain);
    fd.set(
      "industry",
      companyData?.industry_and_business_activities
        ?.primary_industry_classification ?? ""
    );
    fd.set("size", "1");
    fd.set(
      "location",
      companyData?.core_company_identifiers?.headquarters_address?.city ?? ""
    );
    fd.set(
      "companyStructure",
      JSON.stringify({
        type: "single",
        companyCount: 1,
        departments: ["Sustainability"],
        jurisdictions: [],
      })
    );
    // Abteilung für user_profiles
    fd.set("department", "Sustainability");
    fd.set("companyUrl", "");
    fd.set("uploadedFileCount", "0");
    fd.set("uploadedFileIds", "[]");
    fd.set("aiConversation", "[]");
    fd.set("salutation", firstName);
    fd.set("jobTitle", "");

    startTransition(() => formAction(fd));
  };

  /* Register-Status überwachen (Toasts + Redirect) */
  useEffect(() => {
    if (!stateRaw) return;
    switch (stateRaw.status) {
      case "user_exists":
        toast({ type: "error", description: t("accountExists") });
        router.replace("/login");
        break;
      case "invalid_data":
        toast({ type: "error", description: t("invalidData") });
        router.replace("/register");
        break;
      case "failed":
        toast({ type: "error", description: t("registrationFailed") });
        break;
      case "success":
        toast({ type: "success", description: t("welcomeAboard") });
        router.replace("/");
    }
  }, [stateRaw, router, t]);

  const loading =
    isPending ||
    stateRaw.status === "idle" ||
    stateRaw.status === "in_progress";

  /* Hilfskomponente für Company-Block */
  const CompanyCard = () => {
    const id = companyData?.core_company_identifiers ?? {};
    const addr = id.headquarters_address ?? {};
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-2">
          <Building2 className="size-5 text-hawkeye-600" />
          {t("companyData")}
        </h2>
        {id.company_name && (
          <div className="flex items-center gap-2">
            <Building2 className="size-4 text-slate-500" />
            {id.company_name}
          </div>
        )}
        {id.legal_form && (
          <div className="flex items-center gap-2">
            <Landmark className="size-4 text-slate-500" />
            {t("legalForm")}: {id.legal_form}
          </div>
        )}
        {id.domain && (
          <div className="flex items-center gap-2">
            <Globe className="size-4 text-slate-500" />
            {id.domain}
          </div>
        )}
        {id.commercial_register_number && (
          <div className="flex items-center gap-2">
            <Landmark className="size-4 text-slate-500" />
            {t("registerNumber")}: {id.commercial_register_number}
          </div>
        )}
        {id.vat_id && (
          <div className="flex items-center gap-2">
            <BadgeEuro className="size-4 text-slate-500" />
            {t("vatId")}: {id.vat_id}
          </div>
        )}
        {addr.street && (
          <div className="flex items-center gap-2">
            <Globe className="size-4 text-slate-500" />
            {addr.street}, {addr.postal_code} {addr.city}, {addr.country}
          </div>
        )}
      </div>
    );
  };

  /* ─────────────────────────────────────────────── */
  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-slate-50 p-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="w-full max-w-lg">
        <AnimatePresence mode="wait">
          {/* ─────────── Step 0 – Vorname ─────────── */}
          {step === 0 && (
            <motion.div
              key="0"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <h1 className="text-3xl font-bold">
                {t("welcomeTitle")}
              </h1>
              <p className="mt-2 text-lg text-slate-600 dark:text-slate-400">
                {t("welcomeSubtitle")}
              </p>
              <form
                onSubmit={handleNameSubmit}
                className="mt-6 flex items-center gap-2"
              >
                <Input
                  ref={inputRef}
                  placeholder={t("yourFirstName")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  size="lg"
                />
                <Button type="submit" size="icon" className="size-12">
                  <ArrowRight className="size-5" />
                </Button>
              </form>
              {error && (
                <p className="mt-2 text-sm text-red-500 dark:text-red-400">
                  {error}
                </p>
              )}
            </motion.div>
          )}

          {/* ─────────── Step 1 – E-Mail & Passwort ─────────── */}
          {step === 1 && (
            <motion.div
              key="1"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <h1 className="text-3xl font-bold">{t("greeting", { name: firstName }).replace("Hi, ", "").replace("!", "")}!</h1>
              <p className="mt-2 text-lg text-slate-600 dark:text-slate-400">
                {t("weNeedCredentials")}
              </p>

              <form onSubmit={handleCredsSubmit} className="mt-6 space-y-4">
                <Label>{t("emailAddress")}</Label>
                <Input
                  ref={inputRef}
                  type="email"
                  placeholder={t("emailAddress")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="lg"
                />

                <Label>{t("password")}</Label>
                <Input
                  type="password"
                  placeholder={t("passwordMinLength")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  size="lg"
                />

                <Label>{t("department")}</Label>
                <Select disabled defaultValue="sustainability">
                  <SelectTrigger
                    className="h-12 w-full rounded-md border px-3 py-2 text-lg
                      border-slate-300 bg-white text-slate-900
                      dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100
                      cursor-not-allowed opacity-70"
                  >
                    <SelectValue>Sustainability</SelectValue>
                  </SelectTrigger>
                </Select>
                <Separator />

                <Button type="submit" className="h-12 w-full text-base">
                  {t("continue")} <ArrowRight className="ml-2 size-5" />
                </Button>
              </form>
              {error && (
                <p className="mt-2 text-sm text-red-500 dark:text-red-400">
                  {error}
                </p>
              )}
            </motion.div>
          )}

          {/* ─────────── Step 2 – „Scraping" Progress ─────────── */}
          {step === 2 && (
            <motion.div
              key="2"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="text-center"
            >
              <h1 className="text-3xl font-bold">{t("analyzingCompany")}</h1>
              <p className="mt-2 text-lg text-slate-600 dark:text-slate-400">
                {t("analyzingCompanySubtitle")}
              </p>
              <ul className="mt-8 space-y-3 text-left">
                {simulationTasks.map((task) => (
                  <li key={task.key} className="flex items-center gap-3 text-lg">
                    {completedSteps.includes(task.key) ? (
                      <CheckCircle2 className="size-6 text-green-500" />
                    ) : (
                      <Loader2 className="size-6 animate-spin text-slate-400" />
                    )}
                    <span
                      className={
                        completedSteps.includes(task.key)
                          ? "line-through text-slate-500 dark:text-slate-500/70"
                          : ""
                      }
                    >
                      {task.label}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* ─────────── Step 3 – Preview / Closed-Beta ─────────── */}
          {step === 3 && (
            <motion.div
              key="3"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {companyData ? (
                <>
                  {/* User-Block */}
                  <h1 className="text-3xl font-bold mb-4">
                    {t("verifyData")}
                  </h1>

                  <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <h2 className="text-xl font-semibold flex items-center gap-2 mb-2">
                      <User className="size-5 text-hawkeye-600" />
                      {t("yourData")}
                    </h2>
                    <div className="flex items-center gap-2">
                      <User className="size-4 text-slate-500" /> {firstName}
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="size-4 text-slate-500" /> {email}
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="size-4 text-slate-500" /> {t("department")}:
                      Sustainability
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="size-4 text-slate-500" /> {t("userType")}:{" "}
                      {userType === "rare" ? t("pioneer") : t("teamMember")}
                    </div>
                  </div>
                  <Separator className="my-4" />

                  {/* Company-Block */}
                  <CompanyCard />

                  <Button
                    onClick={handleCreateAccount}
                    className="mt-6 h-12 w-full"
                  >
                    {t("createAccount")}
                  </Button>
                </>
              ) : (
                /* Closed-Beta Fallback */
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-400 dark:bg-amber-950">
                  <h2 className="text-xl font-bold text-amber-900 dark:text-amber-300">
                    {t("companyNotActivated")}
                  </h2>
                  <p className="mt-2 text-amber-800 dark:text-amber-200">
                    {t("companyNotActivatedMessage")}
                  </p>
                  <Button
                    variant="secondary"
                    className="mt-4 bg-secondary text-secondary-foreground
                      dark:bg-slate-800 dark:text-slate-200/70"
                    onClick={() => {
                      window.location.href =
                        "mailto:info@hawkeye.legal?subject=Request for CLOSED BETA Test Access";
                    }}
                  >
                    {t("contactUs")} <ArrowRight className="ml-2 size-4" />
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* ─────────── Step 4 – Spinner ─────────── */}
          {step === 4 && loading && (
            <motion.div
              key="4"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="flex flex-col items-center gap-6"
            >
              <Loader2 className="size-12 animate-spin text-hawkeye-600" />
              <p className="text-lg text-slate-700 dark:text-slate-300">
                {t("creatingAccount")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
